import React, {useEffect, useState} from 'react';
import {useStoreActions, useStoreState} from '@/store';
import {buildConversationsURL, buildConversationsCallsURL, buildConversationsChatsURL, buildConversationsEmailsURL, buildConversationsFeedbacksURL, buildEventDetailURL, buildEventURL} from '@/utils/urlBuilder';
import EventDetailBlock from './EventDetailBlock';
import Breadcrumb from '@/components/navigation/Breadcrumb';

interface EventDetailViewWithMenuContextProps {
    activeEventId: number;
    [key: string]: any;
}

// Helper to keep the "event detail" breadcrumb link in the current Conversations context
const getEventDetailHrefForCurrentContext = (activeEventId: number): string => {
    const path = window.location?.pathname || '';

    // If we're already under a Conversations section, keep using that path
    if (
        path.startsWith('/app/conversations/calls/') ||
        path.startsWith('/app/conversations/chats/') ||
        path.startsWith('/app/conversations/emails/') ||
        path.startsWith('/app/conversations/feedbacks/')
    ) {
        return path;
    }

    // Fallback: global event detail URL
    return buildEventDetailURL(activeEventId);
};

const EventDetailViewWithMenuContext: React.FC<EventDetailViewWithMenuContextProps> = ({ 
    activeEventId, 
    ...props 
}) => {
    const setActiveLinkRoute = useStoreActions((actions: any) => actions.sidebarMenu.setActiveLinkRoute);
    const setEventDetails = useStoreActions((actions: any) => actions.eventDetail.setEventDetails);
    const [breadcrumbItems, setBreadcrumbItems] = useState<any[]>([]);
    
    const eventDetails = useStoreState((state: any) => state.eventDetail?.eventDetails);
    
    useEffect(() => {
        const updateBreadcrumbsAndMenu = (data: any) => {
            if (!data?.eventTypeName) {
                setBreadcrumbItems([
                    {
                        key: 'conversations',
                        text: 'Conversations',
                        href: buildConversationsURL()
                    },
                    {
                        key: 'calls',
                        text: 'Calls',
                        href: buildConversationsCallsURL()
                    },
                    {
                        key: 'event-detail',
                        text: `Event ${activeEventId}`,
                        href: getEventDetailHrefForCurrentContext(activeEventId)
                    }
                ]);
                setActiveLinkRoute(buildConversationsCallsURL());
                return;
            }

            let menuRoute = '';
            let eventTypeText = '';
            let eventTypeRoute = '';
            
            switch (data.eventTypeName) {
                case 'call':
                    menuRoute = buildConversationsCallsURL();
                    eventTypeText = 'Calls';
                    eventTypeRoute = buildConversationsCallsURL();
                    break;
                case 'chat':
                    menuRoute = buildConversationsChatsURL();
                    eventTypeText = 'Chats';
                    eventTypeRoute = buildConversationsChatsURL();
                    break;
                case 'email':
                    menuRoute = buildConversationsEmailsURL();
                    eventTypeText = 'Emails';
                    eventTypeRoute = buildConversationsEmailsURL();
                    break;
                case 'feedback':
                    menuRoute = buildConversationsFeedbacksURL();
                    eventTypeText = 'Feedback';
                    eventTypeRoute = buildConversationsFeedbacksURL();
                    break;
                default:
                    menuRoute = buildConversationsCallsURL();
                    eventTypeText = 'Calls';
                    eventTypeRoute = buildConversationsCallsURL();
            }
            
            let eventTitle = `Event ${activeEventId}`;
            
            if (data.datetime) {
                const eventDate = new Date(data.datetime).toLocaleDateString();
                eventTitle = `Event ${activeEventId} (${eventDate})`;
            }
            
            if (data.channel?.name) {
                eventTitle = `${data.channel.name} - ${eventTitle}`;
            }
            
            const dynamicBreadcrumbs = [
                {
                    key: 'conversations',
                    text: 'Conversations',
                    href: buildConversationsURL()
                },
                {
                    key: 'event-type',
                    text: eventTypeText,
                    href: eventTypeRoute
                },
                {
                    key: 'event-detail',
                    text: eventTitle,
                    href: getEventDetailHrefForCurrentContext(activeEventId)
                }
            ];
            
            setBreadcrumbItems(dynamicBreadcrumbs);
            setActiveLinkRoute(menuRoute);
        };

        if (eventDetails) {
            updateBreadcrumbsAndMenu(eventDetails);
        } else {
            const fetchEventDetails = async () => {
                try {
                    const response = await fetch(buildEventURL('eventDetails', activeEventId));
                    const data = await response.json();
                    setEventDetails(data);
                    updateBreadcrumbsAndMenu(data);
                } catch (error) {
                    console.error('Failed to fetch event details for menu context:', error);
                    setBreadcrumbItems([
                        {
                            key: 'conversations',
                            text: 'Conversations',
                            href: buildConversationsURL()
                        },
                        {
                            key: 'calls',
                            text: 'Calls',
                            href: buildConversationsCallsURL()
                        },
                        {
                            key: 'event-detail',
                            text: `Event ${activeEventId}`,
                            href: getEventDetailHrefForCurrentContext(activeEventId)
                        }
                    ]);
                    setActiveLinkRoute(buildConversationsCallsURL());
                }
            };
            fetchEventDetails();
        }
    }, [eventDetails, activeEventId, setActiveLinkRoute, setEventDetails]);
    
    return (
        <div>
            {breadcrumbItems.length > 0 && (
                <Breadcrumb items={breadcrumbItems} />
            )}
            <div className="breadcrumb-content mt-4">
                <EventDetailBlock {...props} activeEventId={activeEventId} />
            </div>
        </div>
    );
};

export default EventDetailViewWithMenuContext;
